import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Power,
  Shield,
  UserCheck,
  UserX,
  Mail,
  Calendar,
  LayoutGrid,
  List,
  Eye,
} from 'lucide-react'
import { userService, UserStats } from '../../services/userService'
import { User } from '../../types'
import { ConfirmDialog, useConfirmDialog } from '../../components/ui/ConfirmDialog'
import { UserFormModal } from '../../components/users/UserFormModal'
import { useAuth } from '../../contexts/AuthContext'
import { extractApiErrors } from '../../utils/errorHandler'
import { formatDate } from '../../utils/dateUtils'

// Hook para detectar si es móvil
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}

// Componente Card para vista móvil
interface UserCardProps {
  user: User
  currentUserId: string | undefined
  onView: (user: User) => void
  onToggleActive: (user: User) => void
  onEdit: (user: User) => void
  onDelete: (user: User) => void
}

function UserCard({ user, currentUserId, onView, onToggleActive, onEdit, onDelete }: UserCardProps) {

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 shadow-sm ${!user.isActive ? 'opacity-60' : ''}`}>
      {/* Header con avatar y nombre */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate flex items-center gap-2">
              {user.name}
              {user.id === currentUserId && (
                <span className="badge badge-primary text-xs">Tú</span>
              )}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {user.email}
            </p>
          </div>
        </div>
        <span className={`badge ${user.isActive ? 'badge-success' : 'badge-gray'} flex-shrink-0`}>
          {user.isActive ? 'Activo' : 'Inactivo'}
        </span>
      </div>

      {/* Info adicional */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
        <Calendar className="h-3.5 w-3.5" />
        <span>Creado el {formatDate(user.createdAt)}</span>
      </div>

      {/* Botones de acción */}
      <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-slate-700">
        <button
          onClick={() => onView(user)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-400"
          title="Ver detalle"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          onClick={() => onToggleActive(user)}
          disabled={user.id === currentUserId}
          className={`flex-1 btn py-2 text-xs ${
            user.isActive
              ? 'btn-secondary'
              : 'bg-success-50 text-success-700 hover:bg-success-100 dark:bg-success-900/20 dark:text-success-400'
          } ${user.id === currentUserId ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Power className="h-3.5 w-3.5 mr-1" />
          {user.isActive ? 'Desactivar' : 'Activar'}
        </button>
        <button
          onClick={() => onEdit(user)}
          className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400"
          title="Editar"
        >
          <Edit2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(user)}
          disabled={user.id === currentUserId}
          className={`p-2 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-lg text-error-600 dark:text-error-400 ${
            user.id === currentUserId ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title="Eliminar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export function UsersPage() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const isMobile = useIsMobile()
  const [users, setUsers] = useState<User[]>([])
  const [stats, setStats] = useState<UserStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)

  const confirmDialog = useConfirmDialog()

  const handleView = (user: User) => {
    navigate(`/configuration/users/${user.id}`)
  }

  // En desktop, por defecto tabla; en móvil, por defecto cards
  useEffect(() => {
    setViewMode(isMobile ? 'cards' : 'table')
  }, [])

  useEffect(() => {
    loadUsers()
    loadStats()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      // Solo cargar administradores
      const response = await userService.getAll({ limit: 100, role: 'ADMIN' })
      setUsers(response.users)
    } catch (error) {
      toast.error('Error al cargar los administradores')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const data = await userService.getStats()
      setStats(data)
    } catch (error) {
      console.error(error)
    }
  }

  const handleToggleActive = async (user: User) => {
    // Prevent disabling own account
    if (user.id === currentUser?.id) {
      toast.error('No puedes desactivar tu propia cuenta')
      return
    }

    try {
      const updated = await userService.toggleActive(user.id)
      toast.success(`Usuario ${updated.isActive ? 'activado' : 'desactivado'}`)
      loadUsers()
      loadStats()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    }
  }

  const handleDelete = async (user: User) => {
    // Prevent deleting own account
    if (user.id === currentUser?.id) {
      toast.error('No puedes eliminar tu propia cuenta')
      return
    }

    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar usuario',
      message: `¿Estás seguro de eliminar al usuario "${user.name}"? El usuario será desactivado.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await userService.delete(user.id)
      toast.success('Usuario eliminado correctamente')
      loadUsers()
      loadStats()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    }
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setShowFormModal(true)
  }

  const handleCreate = () => {
    setEditingUser(null)
    setShowFormModal(true)
  }

  const handleFormSuccess = () => {
    setShowFormModal(false)
    setEditingUser(null)
    loadUsers()
    loadStats()
  }

  // Filter users
  const filteredUsers = users.filter((user) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      if (
        !user.name.toLowerCase().includes(search) &&
        !(user.email || '').toLowerCase().includes(search)
      ) {
        return false
      }
    }
    if (filterActive !== '' && user.isActive !== filterActive) return false
    return true
  })


  return (
    <div className="animate-slide-in">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Administradores
            </h1>
            <p className="page-subtitle">
              Gestiona los usuarios con permisos de administración del sistema
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Toggle vista cards/tabla - solo visible en desktop */}
            <div className="hidden md:flex rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
                title="Vista de tarjetas"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 transition-colors border-l border-gray-200 dark:border-slate-700 ${
                  viewMode === 'table'
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
                title="Vista de tabla"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={handleCreate}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuevo Administrador</span>
              <span className="sm:hidden">Nuevo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-warning-100 dark:bg-warning-900/30">
              <Shield className="h-6 w-6 text-warning-600 dark:text-warning-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.admins}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total administradores
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-success-100 dark:bg-success-900/30">
              <UserCheck className="h-6 w-6 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter(u => u.isActive).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Administradores activos
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          <select
            value={filterActive === '' ? '' : filterActive ? 'true' : 'false'}
            onChange={(e) =>
              setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
            }
            className="input sm:w-auto"
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <UserX className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No hay administradores
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm || filterActive !== ''
                ? 'No se encontraron administradores con los filtros aplicados'
                : 'Crea el primer administrador del sistema'}
            </p>
            {!searchTerm && filterActive === '' && (
              <button onClick={handleCreate} className="btn btn-primary">
                Crear administrador
              </button>
            )}
          </div>
        </div>
      ) : (isMobile || viewMode === 'cards') ? (
        /* Vista de Cards (siempre en móvil) */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              currentUserId={currentUser?.id}
              onView={handleView}
              onToggleActive={handleToggleActive}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        /* Vista de Tabla */
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Administrador
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Creado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className={`hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${
                      !user.isActive ? 'opacity-60' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            {user.name}
                            {user.id === currentUser?.id && (
                              <span className="badge badge-primary text-xs">Tú</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`badge ${
                          user.isActive ? 'badge-success' : 'badge-gray'
                        }`}
                      >
                        {user.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(user.createdAt)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleView(user)}
                          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700 dark:text-gray-400 transition-colors"
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={user.id === currentUser?.id}
                          className={`p-2 rounded-lg transition-colors ${
                            user.isActive
                              ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                              : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          } ${
                            user.id === currentUser?.id
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          title={user.isActive ? 'Desactivar' : 'Activar'}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          disabled={user.id === currentUser?.id}
                          className={`p-2 rounded-lg text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors ${
                            user.id === currentUser?.id
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <UserFormModal
          user={editingUser}
          onClose={() => {
            setShowFormModal(false)
            setEditingUser(null)
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog.dialogProps} />
    </div>
  )
}
